import {
  Accordion,
  AccordionItemContentProps,
  AccordionItemProps,
  useAccordionItemContext,
} from '@chakra-ui/react';
import React, { FunctionComponent as FC, useId } from 'react';
import { MAccordionCustomButton, MText } from '.';

export interface MSettingAccordionItemProps extends Omit<
  AccordionItemProps,
  'value'
> {
  value?: string;
  children?: React.ReactNode;
  description?: string;
  label?: string;
  accordionPanelProps?: AccordionItemContentProps;
  renderAccordionButton?: ({
    isExpanded,
  }: {
    isExpanded: boolean;
  }) => React.ReactNode;
}

interface MSettingAccordionItemBodyProps {
  description?: string;
  label?: string;
  accordionPanelProps?: AccordionItemContentProps;
  renderAccordionButton?: MSettingAccordionItemProps['renderAccordionButton'];
  children?: React.ReactNode;
}

// `useAccordionItemContext` only resolves inside a descendant of
// `Accordion.Item` — v3 dropped v2's render-prop children (`{({isExpanded})
// => ...}`), so the isExpanded-dependent content moves into its own child
// component instead, same pattern as Track D's `MDropdownActions` internal
// content component reading `useMenuContext()`.
const MSettingAccordionItemBody: FC<MSettingAccordionItemBodyProps> = ({
  description,
  label,
  accordionPanelProps,
  renderAccordionButton,
  children,
}) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      {!!label && (
        <MAccordionCustomButton isExpanded={isExpanded} label={label} />
      )}

      {renderAccordionButton && renderAccordionButton({ isExpanded })}

      <Accordion.ItemContent {...accordionPanelProps}>
        <Accordion.ItemBody>
          {!!description && (
            <MText color="tPurple.dark" mb={4}>
              {description}
            </MText>
          )}

          {children}
        </Accordion.ItemBody>
      </Accordion.ItemContent>
    </>
  );
};

const MSettingAccordionItem: FC<MSettingAccordionItemProps> = React.forwardRef<
  HTMLDivElement,
  MSettingAccordionItemProps
>(
  (
    {
      children,
      description,
      renderAccordionButton,
      label,
      accordionPanelProps,
      value,
      ...rest
    }: MSettingAccordionItemProps,
    ref,
  ) => {
    // v3's `Accordion.Item` requires a `value` (no implicit index-based
    // identity like v2) - most callers render a single standalone item and
    // never had one to give, so fall back to a stable generated id rather
    // than requiring every one of this wrapper's ~30 callers to invent one.
    const generatedValue = useId();

    return (
      <Accordion.Item
        ref={ref}
        borderWidth="1px"
        borderRadius="md"
        borderColor="tGray.back"
        {...rest}
        value={value ?? generatedValue}
      >
        <MSettingAccordionItemBody
          description={description}
          label={label}
          accordionPanelProps={accordionPanelProps}
          renderAccordionButton={renderAccordionButton}
        >
          {children}
        </MSettingAccordionItemBody>
      </Accordion.Item>
    );
  },
);

export { MSettingAccordionItem };
